import React, { useState } from "react";
import { Button } from "@/components/common/Button";
import { Callout } from "@/components/common/Callout";
import { Card } from "@/components/common/Card";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { IconPicker } from "@/components/common/IconPicker";
import { Spinner } from "@/components/common/Spinner";
import { SectionRule } from "components/common/SectionRule";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import {
  useAsking,
  useConfirmAll,
  useQuestions,
  useRun,
} from "@/lib/api/apps/hooks";
import type {
  AnswerRequest,
  AppDetail,
  AppPolicy,
  AskingRequest,
  Question,
} from "@/lib/api/apps/types";
import { AnsweredRow, QuestionCard } from "./question-card";
import { RefusalCallout } from "@/components/authentication/consent/RefusalCallout";
import { endReasonWords } from "../app-format";

/** The round's answers as the asking door takes them: what the person chose,
 *  else the agent's suggestion. */
export function roundAnswers(
  open: Question[],
  chosen: Record<string, AnswerRequest>,
): AskingRequest["answers"] {
  return open.map((q) => ({
    question_id: q.question_id,
    ...(chosen[q.question_id] ?? { option: q.pick }),
  }));
}

const ANSWERING = [
  {
    value: "keep_going" as const,
    label: "Auto-pick",
    Icon: AutoAwesomeOutlinedIcon,
    explain: "Auto-pick: the agent picks answers automatically and continues.",
  },
  {
    value: "wait" as const,
    label: "Wait for me",
    Icon: PersonOutlineOutlinedIcon,
    explain:
      "Wait for me: the agent does not try to resolve the question and leaves it open until you answer.",
  },
];

/** Every question the app asked, as one log (docs/ResearchAppSetup.md § 4):
 *  the open ones on top in the order raised, then the saved answers, newest
 *  first. A round before run 1 and a run's question read the same. */
export function ClarificationsPane({
  app,
  onDone,
  onPolicy,
}: {
  app: AppDetail;
  /** Shown once the asking is over before run 1: on to Interval and limits. */
  onDone?: () => void;
  /** Saves how the agent treats a question nobody has answered yet. */
  onPolicy?: (policy: AppPolicy) => void;
}) {
  const [policy, setPolicy] = useState<AppPolicy>(
    app.policy === "wait" ? "wait" : "keep_going",
  );
  const [hovered, setHovered] = useState<AppPolicy | null>(null);
  // Keeps its words while it fades out.
  const [hint, setHint] = useState<AppPolicy>("keep_going");
  const hover = (option: AppPolicy | null) => {
    setHovered(option);
    if (option) setHint(option);
  };
  const asking = useAsking(app.app_id);
  const confirmAll = useConfirmAll(app.app_id);
  const isAsking = app.step === "ask";
  const round = app.current_run?.kind === "ask" ? app.current_run : null;
  const lastRound = app.runs.find((r) => r.kind === "ask") ?? null;
  const questions = useQuestions(app.app_id, isAsking);
  const events =
    useRun(app.app_id, round?.run_id ?? null, Boolean(round)).data?.events ??
    [];
  const [chosen, setChosen] = useState<Record<string, AnswerRequest>>({});
  const [openId, setOpenId] = useState<string | null>(null);
  const [saved, setSaved] = useState<ReadonlySet<string>>(new Set());
  const [confirming, setConfirming] = useState(false);
  const markSaved = (ids: string[]) => setSaved((s) => new Set([...s, ...ids]));

  const open = questions.data?.open ?? [];
  const settled = questions.data?.answered ?? [];
  // A round's questions are answered together through the asking door.
  const inRound = open.filter((q) => q.ask_round != null);
  const fromRuns = open.filter((q) => q.ask_round == null);
  const current = openId ?? open[0]?.question_id ?? null;
  const skipped = inRound.filter((q) => chosen[q.question_id]?.skip).length;
  const releasedRows = fromRuns
    .filter((q) => q.holds)
    .reduce((n, q) => n + Number(q.holds_label.match(/\d+/)?.[0] ?? 0), 0);
  const send = (end: boolean) =>
    asking.mutate(
      { answers: roundAnswers(inRound, chosen), end },
      {
        onSuccess: () => {
          markSaved(inRound.map((q) => q.question_id));
          setChosen({});
        },
      },
    );
  const failed =
    isAsking && !round && open.length === 0 && lastRound?.state === "failed";
  const reading = isAsking && !failed && open.length === 0;
  const latest = events[events.length - 1]?.summary;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <SectionRule
          label="Clarifications"
          end={
            onPolicy && (
              <IconPicker
                ariaLabel="Unanswered questions"
                options={ANSWERING}
                value={policy === "wait" ? "wait" : "keep_going"}
                onHover={hover}
                onChange={(next) => {
                  setPolicy(next);
                  onPolicy(next);
                }}
              />
            )
          }
        />
        {onPolicy && (
          <span
            className={`text-fg-muted-regular-small italic transition-opacity duration-150 ${hovered ? "opacity-100" : "opacity-0"}`}
            role="status"
            aria-hidden={!hovered}
          >
            {ANSWERING.find((a) => a.value === hint)?.explain}
          </span>
        )}
        {open.length > 0 && (
          <span className="text-fg-muted">
            The agent looked at your tables and the brief and has a few
            questions. Answer what you can; what you skip, it decides and notes.
          </span>
        )}
      </div>

      {reading && (
        <Card variant="subtle">
          <div className="flex items-center gap-3" aria-live="polite">
            <Spinner size="sm" />
            <span className="text-fg flex-1">
              {app.model_name} is reading your tables and the brief
            </span>
            {latest && (
              <span className="text-mono-small-muted truncate">{latest}</span>
            )}
          </div>
        </Card>
      )}

      {failed && (
        <Callout
          variant="error"
          title="The agent could not ask"
          action={
            <Button
              size="sm"
              variant="secondary"
              loading={asking.isPending}
              onClick={() => asking.mutate({ answers: [] })}
            >
              Try again
            </Button>
          }
        >
          {lastRound?.end_reason
            ? endReasonWords(lastRound.end_reason)
            : "The round failed"}
        </Callout>
      )}

      {open.length > 0 && (
        <div className="flex flex-col gap-3">
          {open.map((q, i) => (
            <QuestionCard
              key={q.question_id}
              appId={app.app_id}
              question={q}
              number={i + 1}
              open={current === q.question_id}
              onToggle={() =>
                setOpenId(current === q.question_id ? "" : q.question_id)
              }
              onChange={
                q.ask_round != null
                  ? (answer) =>
                      setChosen((c) => ({ ...c, [q.question_id]: answer }))
                  : undefined
              }
              onSaved={(id) => markSaved([id])}
            />
          ))}
          {inRound.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-fg-muted-regular-small flex-1">
                {inRound.length === 1
                  ? "1 question"
                  : `${inRound.length} questions`}
                {skipped > 0 && ` · ${skipped} skipped`}
              </span>
              <Button
                variant="secondary"
                disabled={asking.isPending}
                onClick={() => send(true)}
              >
                Skip all
              </Button>
              <Button
                variant="primary"
                loading={asking.isPending}
                onClick={() => send(false)}
              >
                Send answers
              </Button>
            </div>
          )}
          {inRound.length === 0 && fromRuns.length > 1 && (
            <Button
              variant="quiet-link"
              size="sm"
              className="self-end"
              onClick={() => setConfirming(true)}
            >
              {releasedRows > 0
                ? `Confirm all picks · releases ${releasedRows} rows`
                : "Confirm all picks"}
            </Button>
          )}
        </div>
      )}

      {settled.length > 0 && (
        <Card padded={false}>
          {settled.map((q) => (
            <AnsweredRow
              key={q.question_id}
              appId={app.app_id}
              question={q}
              fresh={saved.has(q.question_id)}
              onSaved={(id) => markSaved([id])}
            />
          ))}
        </Card>
      )}

      {!isAsking && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-fg-muted flex-1">
            {settled.length === 0
              ? "The agent had no questions about the brief and the tables."
              : "The agent asks again when you change the brief."}
          </span>
          {onDone && app.status === "setup" && (
            <Button variant="primary" onClick={onDone}>
              Continue
            </Button>
          )}
        </div>
      )}

      <RefusalCallout error={asking.error} />
      <RefusalCallout error={confirmAll.error} />
      <ConfirmDialog
        open={confirming}
        title="Confirm every pick?"
        message={
          releasedRows > 0
            ? `Every open question is answered with the agent's pick, and the ${releasedRows} rows they hold are released.`
            : "Every open question is answered with the agent's pick."
        }
        confirmText="Confirm picks"
        isLoading={confirmAll.isPending}
        onConfirm={() =>
          confirmAll.mutate(undefined, {
            onSuccess: () => {
              markSaved(fromRuns.map((q) => q.question_id));
              setConfirming(false);
            },
          })
        }
        onCancel={() => setConfirming(false)}
      />
    </section>
  );
}
